import React, { useState, useEffect } from 'react';
import { Steps, Button, Form, message } from 'antd';
import { Link } from 'react-router-dom';
import * as CA from './index.styled';
import BusinessVerification from './BusinessVerification';
import ConsentAgreement from './ConsentAgreement';
import DelegateConsent from './DelegateConsent';
import InformationInputExistingUser from './InformationInputExistingUser';
import InformationInputNewCompany from './InformationInputNewCompany';
import InformationInputNewUser from './InformationInputNewUser';
import SignUpCompletion from './SignUpCompletion';
import { AdvertiserSearch } from '@/@types/AdvertiserSearch';
import { ResData } from '@/@types';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import axiosRequest from '@/api';
import { Register } from '@/@types/Register';

const { Step } = Steps;

const CreateAccount: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const [adCategory, setAdCategory] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [inputBizNo, setInputBizNo] = useState({ part1: '', part2: '', part3: '' });
  const [advertiserData, setAdvertiserData] = useState<AdvertiserSearch | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [agreements, setAgreements] = useState({
    agreement1: false,
    agreement2: false,
  });
  const [isAgreed, setIsAgreed] = useState(false);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const [form] = Form.useForm();

  const updateStepData = (data: Record<string, any>) => {
    setStepData(prev => ({ ...prev, ...data }));
    if (data.partner_id) {
      setPartnerId(data.partner_id.toString());
    }
  };

  useEffect(() => {
    const allAgreementsChecked = agreements.agreement1 && agreements.agreement2;
    if (current === 1) {
      setIsStepValid(allAgreementsChecked);
    }
    if (current === steps.findIndex(step => step.title === '위임 동의')) {
      setIsStepValid(isAgreed && partnerId !== null);
    }
  }, [agreements, isAgreed, partnerId, current]);

  const handleSuccess = (data: AdvertiserSearch) => {
    setAdvertiserData(data);
    setIsStepValid(true);
    next();
    alert('이미 가입된 회원입니다. 운영자 등록을 하시려면 다음 단계를 진행해 주세요.');
  };

  const handleNotFound = () => {
    alert('가입된 정보가 없습니다. 운영자 등록을 하시려면 다음 단계를 진행해 주세요.');
    setIsStepValid(true);
    next();
  };

  const handleAgreementChange = (id: string, checked: boolean) => {
    setAgreements(prev => ({ ...prev, [id]: checked }));
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setIsAgreed(e.target.checked);
    setErrorMessage('');
  };

  const getUncheckedAgreements = () => {
    const unchecked = [];
    if (!agreements.agreement1) unchecked.push('[필수] 서비스 이용약관 동의');
    if (!agreements.agreement2) unchecked.push('[필수] 개인정보 수집 및 이용 동의');
    return unchecked;
  };

  const formatBizNo = (bizNo: string) => {
    return bizNo.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
  };

  const getSteps = () => {
    const steps = [
      {
        title: '가입 인증',
        content: (
          <BusinessVerification
            setIsVerified={setIsVerified}
            onSuccess={handleSuccess}
            onNotFound={handleNotFound}
            setInputBizNo={setInputBizNo}
            setAdCategory={setAdCategory}
            setStepValid={setIsStepValid}
            adCategory={adCategory}
            inputBizNo={inputBizNo}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
        ),
      },
      {
        title: '약관 동의',
        content: (
          <ConsentAgreement
            agreements={agreements}
            onAgreementChange={handleAgreementChange}
          />
        ),
      },
    ];

    if (adCategory === 'c' && !advertiserData) {
      steps.push({
        title: '위임 동의',
        content: (
          <DelegateConsent
            isAgreed={isAgreed}
            handleAgreementChange={handleCheckboxChange}
            category={adCategory}
            partnerId={partnerId}
            setPartnerId={setPartnerId}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
        ),
      });
    }

    steps.push({
      title: '정보 입력',
      content: (
        <>
          {advertiserData ? (
            <InformationInputExistingUser
              form={form}
              companyName={advertiserData.name}
              bizNo={formatBizNo(`${inputBizNo.part1}${inputBizNo.part2}${inputBizNo.part3}`)}
              isEmailVerified={isEmailVerified}
              setIsEmailVerified={setIsEmailVerified}
            />
          ) : (
            <>
              {subStep === 0 && (
                <InformationInputNewCompany
                  form={form}
                  stepData={updateStepData}
                  category={adCategory}
                  upperAgency={stepData.upperAgency}
                  inputBizNo={inputBizNo}
                  setStepValid={setIsStepValid}
                />
              )}
              {subStep === 1 && (
                <InformationInputNewUser
                  form={form}
                  stepData={updateStepData}
                  existingData={stepData}
                  isEmailVerified={isEmailVerified}
                  setIsEmailVerified={setIsEmailVerified}
                />
              )}
            </>
          )}
        </>
      ),
    });

    steps.push({
      title: '가입 완료',
      content: <SignUpCompletion />,
    });

    return steps;
  };

  const steps = getSteps();

  const next = async () => {
    if (isStepValid) {
      if (current === steps.findIndex(step => step.title === '정보 입력')) {
        if (advertiserData) {
          try {
            const values = await form.validateFields();
            updateStepData(values);
            if (isEmailVerified) {
              handleSubmitForm();
            } else {
              message.error('이메일 중복 확인을 완료해주세요.');
            }
          } catch (error) {
            message.error('모든 필드를 올바르게 입력해주세요.');
          }
        } else {
          try {
            const values = await form.validateFields();
            updateStepData(values);
            if (subStep === 0) {
              setSubStep(subStep + 1);
            } else {
              if (isEmailVerified) {
                handleSubmitForm();
              } else {
                message.error('이메일 중복 확인을 완료해주세요.');
              }
            }
          } catch (error) {
            message.error('모든 필드를 올바르게 입력해주세요.');
          }
        }
      } else {
        setCurrent(current + 1);
      }
    } else {
      if (current === 1) {
        const uncheckedAgreements = getUncheckedAgreements();
        alert(`다음 항목에 동의해주세요:\n${uncheckedAgreements.join('\n')}`);
      } else if (current === steps.findIndex(step => step.title === '위임 동의')) {
        if (!partnerId) {
          alert('[필수] 대행사를 선택해주세요.');
        } else if (!isAgreed) {
          alert('[필수] 대행사 위임 약관 동의가 필요합니다.');
        }
      }
    }
  };

  const prev = () => {
    setErrorMessage('');
    if (current === steps.findIndex(step => step.title === '정보 입력') && subStep > 0) {
      setSubStep(subStep - 1);
    } else {
      setCurrent(current - 1);
    }
  };

  const handleSubmitForm = async () => {
    if (!isEmailVerified) {
      message.error('이메일 중복 확인을 완료해야 회원가입이 가능합니다.');
      return;
    }

    try {
      const values = await form.validateFields();
      const { email, password, name, department, phone1, phone2, phone3 } = values;
      const phone = `${phone1}${phone2}${phone3}`;
      const bizNo = `${inputBizNo.part1}${inputBizNo.part2}${inputBizNo.part3}`; // '-'를 뺀 나머지 숫자 10자리

      const requestData = {
        email,
        password,
        name,
        department,
        phone,
        advertiser_id: advertiserData?.id ?? 0,
        ad_name: stepData.ad_name,
        ad_category: adCategory,
        ad_biz_no: bizNo, // 여기서 '-'를 뺀 번호를 사용합니다.
        ad_ceo_name: stepData.ad_ceo_name,
        ad_biz_type: stepData.ad_biz_type,
        ad_biz_item: stepData.ad_biz_item,
        ad_address: stepData.ad_address,
        ad_partner_id: partnerId ?? 0,
        ad_biz_enroll_docs: stepData.ad_biz_enroll_docs,
        policy_agree: true,
        mandate_agree: adCategory === 'c',
      };

      const response = await axiosRequest.requestAxios<ResData<Register>>('post', '/v2/register', requestData);

      if (response.data) {
        message.success('회원가입이 완료되었습니다.');
        setCurrent(current + 1);
      } else {
        message.error('회원가입에 실패했습니다.');
      }
    } catch (error) {
      message.error('입력 필드를 확인해주세요.');
    }
  };

  return (
    <CA.Wrapper>
      <CA.StyledLink to="/">wereward</CA.StyledLink>
      <CA.SignUpTitle>회원 가입</CA.SignUpTitle>
      <CA.CustomSteps current={current}>
        {steps.map((item, index) => (
          <CA.CustomStep key={index} isActive={index === current}>
            {item.title}
          </CA.CustomStep>
        ))}
      </CA.CustomSteps>
      <CA.StepsContent>{steps[current].content}</CA.StepsContent>
      {current < steps.length - 1 && (
        <CA.StepsAction hasPrevButton={current > 0} hasSingleButton={!(current === 1 || current > 1)}>
          {current > 0 && (
            <Button style={{ width: '80px' }} onClick={() => prev()}>
              이전
            </Button>
          )}
          <Button
            style={{
              width: '80px',
              backgroundColor: isStepValid ? '#225F9B' : '#ddd',
              color: '#fff',
            }}
            type="primary"
            onClick={current === steps.findIndex(step => step.title === '정보 입력') && subStep === 1 ? handleSubmitForm : next}
          >
            {current === steps.findIndex(step => step.title === '정보 입력') && (advertiserData || subStep === 1)
              ? '회원가입'
              : '다음'}
          </Button>
        </CA.StepsAction>
      )}
    </CA.Wrapper>
  );
};

export default CreateAccount;
